import { useEffect, useState, useRef } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

const AnimatedStat = ({ end, suffix = "", prefix = "" }: { end: number; suffix?: string; prefix?: string }) => {
  const [count, setCount] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const duration = 2000;
    let startTime: number | null = null;

    const animate = (currentTime: number) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [isVisible, end]);

  return (
    <div ref={ref}>
      {prefix}{count.toLocaleString()}{suffix}
    </div>
  );
};

const StatsSection = () => {
  const { t } = useLanguage();

  const stats = [
    { value: 500, suffix: "+", label: t("stats.clients") },
    { value: 50, suffix: "M", label: t("stats.tracked") },
    { value: 5, suffix: "x", label: t("stats.accuracy") },
    { value: 55, suffix: "%", prefix: "~", label: t("stats.timeSaved") }
  ];

  return (
    <section aria-label={t("stats.sectionLabel") !== "stats.sectionLabel" ? t("stats.sectionLabel") : "Key Statistics"} className="py-10 lg:py-12 px-2">
      <div className="bg-gradient-primary rounded-[31px] py-[67px] px-8 lg:px-12 relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, hsl(var(--primary-foreground)) 1px, transparent 0)`,
            backgroundSize: '32px 32px'
          }} />
        </div>
        <div className="container mx-auto max-w-7xl">
          <h2 className="sr-only">{t("stats.sectionLabel") !== "stats.sectionLabel" ? t("stats.sectionLabel") : "Our Impact in Numbers"}</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 relative z-10">
            {stats.map((stat, index) => (
              <div
                key={stat.label}
                className="text-center"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="text-4xl sm:text-5xl lg:text-6xl font-bold text-primary-foreground mb-2">
                  <AnimatedStat end={stat.value} suffix={stat.suffix} prefix={stat.prefix} />
                </div>
                <p className="text-primary-foreground/70 text-sm sm:text-base">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
